import { useState, useRef } from 'react';
import { ChevronDown, ChevronUp, Upload, Download, CheckCircle2 } from 'lucide-react';
import API from '../../lib/api';

const STATUS_COLORS = {
  completed:   'bg-green-100 text-green-700',
  in_progress: 'bg-orange-100 text-orange-700',
  pending:     'bg-gray-100 text-gray-500',
  overdue:     'bg-red-100 text-red-700',
};

export default function RecurringCycleCard({ cycle, orderId, onRefresh }) {
  const [open, setOpen]         = useState(false);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving]     = useState(false);
  const [notes, setNotes]       = useState(cycle.notes || '');
  const fileRef = useRef(null);

  const updateStatus = async (status) => {
    setSaving(true);
    try {
      await API.put(`/admin/orders/${orderId}/cycles/${cycle.month_key}`, { status, notes });
      if (onRefresh) await onRefresh();
    } catch (err) { console.error(err); }
    setSaving(false);
  };

  const saveNotes = async () => {
    setSaving(true);
    try {
      await API.put(`/admin/orders/${orderId}/cycles/${cycle.month_key}`, { status: cycle.status, notes });
      if (onRefresh) await onRefresh();
    } catch (err) { console.error(err); }
    setSaving(false);
  };

  const handleUpload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('deliverable', file);
      await API.post(`/admin/orders/${orderId}/cycles/${cycle.month_key}/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (fileRef.current) fileRef.current.value = '';
      if (onRefresh) await onRefresh();
    } catch (err) { console.error(err); }
    setUploading(false);
  };

  const docCount = cycle.documents?.length || 0;

  return (
    <div className="rounded-xl border border-[#E8E6F8] overflow-hidden">
      {/* Header */}
      <div
        className="flex items-center justify-between p-4 bg-white cursor-pointer hover:bg-[#F5F4FB]"
        onClick={() => setOpen(!open)}
      >
        <div className="flex items-center gap-3">
          <span className="text-sm font-heading font-semibold text-[#1A1A2E]">
            {cycle.month_key}
          </span>
          <span className={`text-[10px] px-2 py-0.5 rounded-full font-body font-medium ${STATUS_COLORS[cycle.status] || 'bg-gray-100 text-gray-500'}`}>
            {cycle.status?.replace('_', ' ') || 'pending'}
          </span>
          {docCount > 0 && (
            <span className="text-[10px] text-[#6B7280] font-body">{docCount} doc{docCount > 1 ? 's' : ''}</span>
          )}
        </div>
        {open ? <ChevronUp className="h-4 w-4 text-[#9CA3AF]" /> : <ChevronDown className="h-4 w-4 text-[#9CA3AF]" />}
      </div>

      {open && (
        <div className="border-t border-[#E8E6F8] p-4 space-y-4 bg-[#F5F4FB]">
          {/* Status buttons */}
          <div>
            <p className="text-xs font-body text-[#6B7280] mb-2">Update Status</p>
            <div className="flex gap-2 flex-wrap">
              {['pending', 'in_progress', 'completed', 'overdue'].map((s) => (
                <button
                  key={s}
                  onClick={() => updateStatus(s)}
                  disabled={saving}
                  className={`text-xs font-body px-3 py-1 rounded-full border transition-all ${
                    cycle.status === s
                      ? 'bg-[#6C63FF] text-white border-[#6C63FF]'
                      : 'border-[#E8E6F8] text-[#6B7280] bg-white hover:border-[#6C63FF]/30'
                  }`}
                >
                  {s.replace('_', ' ')}
                </button>
              ))}
            </div>
          </div>

          {/* Documents */}
          {cycle.documents?.length > 0 && (
            <div>
              <p className="text-xs font-body text-[#6B7280] mb-2">Deliverables</p>
              <div className="space-y-1">
                {cycle.documents.map((d, i) => (
                  <div key={i} className="flex items-center justify-between bg-white p-2 rounded-lg border border-[#E8E6F8]">
                    <span className="text-xs font-body text-[#4A4A60] truncate">{d.original_name || d.name || 'Document'}</span>
                    <button
                      onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}${d.download_url}`, '_blank', 'noopener')}
                      className="text-[#6C63FF] flex-shrink-0 ml-2"
                    >
                      <Download className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Upload deliverable */}
          <div>
            <p className="text-xs font-body text-[#6B7280] mb-2">Upload Deliverable</p>
            <div className="flex items-center gap-2">
              <input
                ref={fileRef}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.xlsx,.xls,.doc,.docx"
                className="flex-1 text-xs font-body text-[#6B7280] file:mr-2 file:py-1 file:px-2.5 file:rounded-lg file:border-0 file:bg-[#6C63FF]/10 file:text-[#6C63FF] file:text-xs cursor-pointer"
              />
              <button
                onClick={handleUpload}
                disabled={uploading}
                className="flex items-center gap-1 text-xs font-body px-3 py-1.5 bg-[#6C63FF] text-white rounded-lg disabled:opacity-60"
              >
                <Upload className="h-3.5 w-3.5" />
                {uploading ? '…' : 'Upload'}
              </button>
            </div>
          </div>

          {/* Notes */}
          <div>
            <p className="text-xs font-body text-[#6B7280] mb-1">Notes</p>
            <div className="flex gap-2">
              <input
                type="text"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Notes for this month…"
                className="flex-1 text-sm font-body border border-[#E8E6F8] rounded-lg px-3 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-[#6C63FF]/50"
              />
              <button
                onClick={saveNotes}
                disabled={saving}
                className="text-xs font-body px-3 py-1.5 bg-white border border-[#E8E6F8] rounded-lg hover:bg-[#F5F4FB] text-[#6B7280]"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
